import React, {KeyboardEvent, MouseEvent, useState} from 'react';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import SuccessfulMonitor from 'shared/ui/icons/SuccessfulMonitor';
import ProblemMonitor from 'shared/ui/icons/ProblemMonitor';
import PauseMonitor from 'shared/ui/icons/PauseMonitor';
import OpenURL from 'shared/ui/icons/General/OpenURL';
import CopyButton from 'shared/ui/IconButton/CopyButton/CopyButton';

import {StatusPageMonitorStatus, IStatusPageViewMonitor} from '../../../model/IStatusPageView';

import StatusMonitorDetails from '../StatusMonitorDetails/StatusMonitorDetails';

import styles from './StatusMonitorRow.module.scss';

interface IStatusMonitorRowProps {
    monitor: IStatusPageViewMonitor,
    defaultExpanded?: boolean,
    forceExpanded?: boolean,
}

const STATUS_ICON: Record<StatusPageMonitorStatus, React.ReactNode> = {
    up: <SuccessfulMonitor/>,
    down: <ProblemMonitor/>,
    paused: <PauseMonitor/>,
};

const STATUS_VARIANT: Record<StatusPageMonitorStatus, string> = {
    up: styles.statusUp,
    down: styles.statusDown,
    paused: styles.statusPaused,
};

const STATUS_DOT: Record<StatusPageMonitorStatus, string> = {
    up: styles.dotUp,
    down: styles.dotDown,
    paused: styles.dotPaused,
};

const StatusMonitorRow: React.FC<IStatusMonitorRowProps> = ({
    monitor,
    defaultExpanded = false,
    forceExpanded = false,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const isOpen = expanded || forceExpanded;

    const toggle = () => setExpanded((prev) => !prev);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
        }
    };

    const stop = (event: MouseEvent) => event.stopPropagation();

    const points = monitor.response_times_24h ?? [];

    return (
        <div className={cn(styles.card, isOpen && styles.cardExpanded)}>
            <div
                className={styles.summary}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={toggle}
                onKeyDown={onKeyDown}
            >
                <div className={styles.body}>
                    <div className={styles.top}>
                        <span className={styles.statusIcon}>{STATUS_ICON[monitor.status]}</span>
                        <span className={styles.url} title={monitor.name}>{monitor.name}</span>
                        {monitor.url !== '' && (
                            <>
                                <CopyButton
                                    value={monitor.url}
                                    title={t`Скопировать`}
                                    stopPropagation
                                    className={styles.copyContainer}
                                    tooltipClassName={styles.tooltip}
                                    buttonClassName={styles.copyButton}
                                />
                                <a
                                    className={styles.openLink}
                                    href={monitor.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={t`Открыть в новой вкладке`}
                                    onClick={stop}
                                >
                                    <OpenURL/>
                                </a>
                            </>
                        )}
                        <span className={styles.status}>
                            <span className={cn(styles.statusBadge, STATUS_VARIANT[monitor.status])}>
                                {monitor.last_message}
                            </span>
                        </span>
                    </div>

                    {!isOpen && (
                        <div className={styles.uptime}>
                            <div className={styles.uptimeInfo}>
                                <span className={styles.uptimeLabel}>{t`Доступность за сутки`}</span>
                                <span className={styles.uptimePercent}>
                                    <span className={cn(styles.dot, STATUS_DOT[monitor.status])}/>
                                    {monitor.availability1}%
                                </span>
                            </div>
                            {points.length > 0 && (
                                <div className={styles.bars}>
                                    {points.map((point, index) => (
                                        <span
                                            key={`${point.name}-${index}`}
                                            className={cn(
                                                styles.bar,
                                                monitor.status === 'paused'
                                                    ? styles.barPaused
                                                    : point.value > 0 ? styles.barUp : styles.barDown,
                                            )}
                                            title={point.name}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <span className={cn(styles.chevron, isOpen && styles.chevronOpen)} aria-hidden="true"/>
            </div>

            {isOpen && <StatusMonitorDetails monitor={monitor}/>}
        </div>
    );
};

export default StatusMonitorRow;
